import React from 'react';

import {
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { SALESFORCE_FIELD_MAX_LENGTH, audMoney, exceedsFieldLength } from '../constants';
import type { RoomAssetLink } from '../tabs/packageOptions/types';

import ImportNameField from './ImportNameField';
import type { ImportNameEdits } from './excelImportModel';
import { CREATE_ASSET, type RoomChoices } from './roomMapping';
import type { ParsedDealOption } from './types';

type ImportRoomsStepProps = {
  assets: Array<RoomAssetLink>;
  choices: RoomChoices;
  dealOptions: Array<ParsedDealOption>;
  nameEdits: ImportNameEdits;
  onNameChange: (key: string, name: string) => void;
  onChange: (roomType: string, assetId: string) => void;
  roomTypes: Array<string>;
  sharingAnAsset: Set<string>;
};

function staysOf(dealOptions: Array<ParsedDealOption>, roomType: string): string {
  return dealOptions
    .filter((dealOption) => dealOption.roomType === roomType)
    .map(({ nights, sell }) => `${nights}N ${audMoney(sell)}`)
    .join(' · ');
}

export default function ImportRoomsStep({
  assets,
  choices,
  dealOptions,
  nameEdits,
  onNameChange,
  onChange,
  roomTypes,
  sharingAnAsset,
}: ImportRoomsStepProps) {
  return (
    <>
      <Typography color="text.secondary" variant="body2">
        Choose the room each Excel room type loads onto. Only base packages are imported: add upgrades and bundles on
        Package Options afterwards.
      </Typography>
      <Table size="small" sx={{ mt: 2 }}>
        <TableHead>
          <TableRow>
            <TableCell>Excel room type</TableCell>
            <TableCell>Stays</TableCell>
            <TableCell>Loads onto</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {roomTypes.map((roomType) => (
            <TableRow key={roomType}>
              <TableCell>{roomType}</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{staysOf(dealOptions, roomType)}</TableCell>
              <TableCell sx={{ minWidth: 320 }}>
                <Stack spacing={1}>
                  <TextField
                    error={sharingAnAsset.has(roomType)}
                    fullWidth
                    helperText={
                      sharingAnAsset.has(roomType) ? 'Another room type already loads onto this room' : undefined
                    }
                    onChange={(event) => onChange(roomType, event.target.value)}
                    select
                    size="small"
                    value={choices[roomType] ?? CREATE_ASSET}
                  >
                    <MenuItem value={CREATE_ASSET}>{`New room "${roomType}"`}</MenuItem>
                    {assets.map((asset) => (
                      <MenuItem key={asset.id} value={asset.id}>
                        {asset.name}
                      </MenuItem>
                    ))}
                  </TextField>
                  {(choices[roomType] ?? CREATE_ASSET) === CREATE_ASSET &&
                    exceedsFieldLength(roomType, SALESFORCE_FIELD_MAX_LENGTH.assetName) && (
                      <ImportNameField
                        kind="roomAsset"
                        label="New room name"
                        nameEdits={nameEdits}
                        onNameChange={onNameChange}
                        original={roomType}
                      />
                    )}
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
}
